import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Navbar, Footer } from "../../components/Layout";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Plus, Edit, Trash2, X, Download, FileText } from "lucide-react";

const emptyForm = { tenant_id: "", property_id: "", start_date: "", end_date: "", rent_monthly: "", charges_monthly: 0, deposit: 0, surface: "", notes: "", status: "draft" };

const STATUS_STYLE = {
  draft: "bg-slate-200 text-slate-700",
  active: "bg-emerald-200 text-emerald-900",
  terminated: "bg-red-100 text-red-700",
};

const fmtMoney = (v) => v != null ? `${parseFloat(v).toLocaleString("fr-FR")} €` : "—";

export default function LeasesAdmin() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [properties, setProperties] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [a, b, c] = await Promise.all([
      api.get("/admin/leases"),
      api.get("/admin/tenants"),
      api.get("/properties"),
    ]);
    setItems(a.data); setTenants(b.data); setProperties(c.data);
  };
  useEffect(() => { load().catch(() => toast.error("Échec de chargement")); }, []);

  const startNew = () => { setEditing("new"); setForm(emptyForm); };
  const startEdit = (L) => {
    setEditing(L.id);
    setForm({
      tenant_id: L.tenant_id, property_id: L.property_id,
      start_date: L.start_date, end_date: L.end_date || "",
      rent_monthly: L.rent_monthly, charges_monthly: L.charges_monthly || 0, deposit: L.deposit || 0,
      surface: L.surface || "", notes: L.notes || "", status: L.status,
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        rent_monthly: parseFloat(form.rent_monthly) || 0,
        charges_monthly: parseFloat(form.charges_monthly) || 0,
        deposit: parseFloat(form.deposit) || 0,
        surface: form.surface ? parseFloat(form.surface) : null,
        end_date: form.end_date || null,
      };
      if (editing === "new") {
        await api.post("/admin/leases", payload);
        toast.success("Bail créé");
      } else {
        const upd = { ...payload }; delete upd.tenant_id; delete upd.property_id;
        await api.put(`/admin/leases/${editing}`, upd);
        toast.success("Bail mis à jour");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Supprimer ce bail ?")) return;
    await api.delete(`/admin/leases/${id}`);
    toast.success("Supprimé");
    load();
  };

  const downloadPdf = async (id) => {
    try {
      const res = await api.get(`/admin/leases/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `bail-${id.slice(0,8)}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Échec PDF"); }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-default)" }}>
      <Navbar />
      <Toaster richColors position="top-right" />
      <section className="max-w-6xl mx-auto px-6 md:px-8 py-12">
        <button onClick={() => navigate("/admin")} className="text-xs font-bold uppercase tracking-widest mb-6 flex items-center gap-2" style={{ color: "var(--text-secondary)" }} data-testid="back-to-dashboard">
          <ArrowLeft className="w-3 h-3" /> Retour au tableau de bord
        </button>
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Baux</h1>
            <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>Gérez les contrats de location actifs et brouillons.</p>
          </div>
          <button onClick={startNew} data-testid="new-lease-btn"
            className="btn-brutal btn-brutal-solar px-5 py-3 font-bold uppercase tracking-wider text-sm flex items-center gap-2">
            <Plus className="w-4 h-4" /> Nouveau bail
          </button>
        </div>

        <div className="bg-[color:var(--bg-surface)] border overflow-x-auto" style={{ borderColor: "var(--border-default)" }}>
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider">Statut</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider">Locataire</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider">Local</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider">Période</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider">Loyer</th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan="6" className="p-12 text-center opacity-50 font-mono">Aucun bail.</td></tr>}
              {items.map(L => (
                <tr key={L.id} className="border-t border-slate-900/10" data-testid={`row-lease-${L.id}`}>
                  <td className="px-4 py-3"><span className={`text-xs font-bold uppercase tracking-wider px-2 py-1 ${STATUS_STYLE[L.status]}`}>{L.status}</span></td>
                  <td className="px-4 py-3 text-xs"><div className="font-semibold">{L.tenant?.name || "—"}</div><div className="text-slate-500">{L.tenant?.email}</div></td>
                  <td className="px-4 py-3 text-xs">{L.property?.title || L.property_id}</td>
                  <td className="px-4 py-3 text-xs font-mono">{L.start_date} → {L.end_date || "∞"}</td>
                  <td className="px-4 py-3 text-xs"><div className="font-bold">{fmtMoney(L.rent_monthly)}</div><div className="text-slate-500">+ {fmtMoney(L.charges_monthly)} ch.</div></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => downloadPdf(L.id)} data-testid={`lease-pdf-${L.id}`} title="Télécharger PDF" className="p-2 border border-slate-900 hover:bg-slate-900 hover:text-white mr-2"><Download className="w-4 h-4"/></button>
                    <button onClick={() => startEdit(L)} data-testid={`edit-lease-${L.id}`} className="p-2 border border-slate-900 hover:bg-slate-900 hover:text-white mr-2"><Edit className="w-4 h-4"/></button>
                    <button onClick={() => remove(L.id)} className="p-2 border border-red-300 text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4"/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {editing && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
            <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-slate-900" onClick={e => e.stopPropagation()} data-testid="lease-modal">
              <div className="flex items-center justify-between p-6 border-b border-slate-900/10">
                <h2 className="font-display font-black text-2xl">{editing === "new" ? "Nouveau bail" : "Modifier le bail"}</h2>
                <button onClick={() => setEditing(null)} className="p-1"><X className="w-5 h-5"/></button>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <F label="Locataire *">
                    <select value={form.tenant_id} onChange={e => setForm({...form, tenant_id: e.target.value})} disabled={editing !== "new"} className="input disabled:opacity-50" data-testid="lm-tenant">
                      <option value="">— Sélectionnez —</option>
                      {tenants.map(t => <option key={t.id} value={t.id}>{t.name} ({t.email})</option>)}
                    </select>
                  </F>
                  <F label="Local *">
                    <select value={form.property_id} onChange={e => setForm({...form, property_id: e.target.value})} disabled={editing !== "new"} className="input disabled:opacity-50" data-testid="lm-property">
                      <option value="">— Sélectionnez —</option>
                      {properties.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                    </select>
                  </F>
                  <F label="Statut">
                    <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="input" data-testid="lm-status">
                      <option value="draft">Brouillon</option>
                      <option value="active">Actif</option>
                      <option value="terminated">Résilié</option>
                    </select>
                  </F>
                  <F label="Surface (m²)"><input type="number" value={form.surface} onChange={e => setForm({...form, surface: e.target.value})} className="input" /></F>
                  <F label="Date début *"><input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} className="input" data-testid="lm-start" /></F>
                  <F label="Date fin (vide = indéterminée)"><input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} className="input" /></F>
                  <F label="Loyer mensuel HT *"><input type="number" step="0.01" value={form.rent_monthly} onChange={e => setForm({...form, rent_monthly: e.target.value})} className="input" data-testid="lm-rent" /></F>
                  <F label="Charges mensuelles"><input type="number" step="0.01" value={form.charges_monthly} onChange={e => setForm({...form, charges_monthly: e.target.value})} className="input" /></F>
                  <F label="Dépôt de garantie"><input type="number" step="0.01" value={form.deposit} onChange={e => setForm({...form, deposit: e.target.value})} className="input" /></F>
                </div>
                <F label="Clauses particulières / notes"><textarea rows={4} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="input resize-none" /></F>
              </div>
              <div className="p-6 border-t border-slate-900/10 flex justify-end gap-3">
                <button onClick={() => setEditing(null)} className="btn-brutal bg-white border border-slate-900 px-4 py-2 text-sm font-bold uppercase tracking-wider">Annuler</button>
                <button onClick={save} disabled={saving || !form.tenant_id || !form.property_id || !form.start_date || !form.rent_monthly} data-testid="lm-save"
                  className="btn-brutal btn-brutal-solar px-5 py-2 text-sm font-bold uppercase tracking-wider disabled:opacity-50">
                  {saving ? "..." : (editing === "new" ? "Créer" : "Enregistrer")}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}

const F = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 block">{label}</span>
    {children}
  </label>
);
